import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  Text,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import { useAuth } from 'contexts/AuthContext';
import authService from 'services/authService';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  CollapsedSidebarItem,
  CollapsedIconButton,
} from './CollapsedSidebarItem';
import StockAlertsMenu from './StockAlertsMenu';

const getUserInitials = (user) => {
  if (!user) return 'U';
  if (user.username) return user.username.substring(0, 2).toUpperCase();
  if (user.name) {
    const names = user.name.split(' ');
    if (names.length >= 2) return (names[0][0] + names[1][0]).toUpperCase();
    return names[0].substring(0, 2).toUpperCase();
  }
  if (user.user) return user.user.substring(0, 2).toUpperCase();
  return 'U';
};

const getDisplayName = (user) => {
  if (!user) return 'Usuario';
  return user.name || user.username || user.user || 'Usuario';
};

export default function SidebarUserMenu({ collapsed }) {
  const { colorMode, toggleColorMode } = useColorMode();
  const { logout } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const activeBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );

  useEffect(() => {
    setCurrentUser(authService.getCurrentUser());
  }, []);

  const handleLogout = () => {
    logout();
    authService.clearUserData();
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  };

  const userMenuContent = (
    <>
      <Text
        px={4}
        pt={3}
        pb={2}
        fontSize="sm"
        fontWeight="700"
        color={textColor}
        borderBottom="1px solid"
        borderColor={borderColor}
      >
        Hola, {getDisplayName(currentUser)}
      </Text>
      <Box
        as="button"
        type="button"
        w="100%"
        textAlign="left"
        px={4}
        py={3}
        fontSize="sm"
        color="red.500"
        cursor="pointer"
        bg="transparent"
        border="none"
        onClick={handleLogout}
        _hover={{ bg: 'red.50' }}
      >
        Cerrar sesión
      </Box>
    </>
  );

  if (collapsed) {
    return (
      <Flex
        direction="column"
        align="center"
        w={`${SIDEBAR_COLLAPSED_WIDTH}px`}
        gap={1}
      >
        <CollapsedSidebarItem>
          <Box display="inline-flex" lineHeight={0}>
            <Popover
              placement="right-end"
              gutter={12}
              isLazy
              lazyBehavior="keepMounted"
              strategy="fixed"
            >
              <PopoverTrigger>
                <CollapsedIconButton
                  bg="transparent"
                  title={getDisplayName(currentUser)}
                  aria-label="Menú de usuario"
                  _hover={{ bg: hoverBg }}
                  _active={{ bg: activeBg }}
                >
                  <Avatar
                    color="white"
                    name={getUserInitials(currentUser)}
                    bg="#003064"
                    size="sm"
                    w="32px"
                    h="32px"
                  />
                </CollapsedIconButton>
              </PopoverTrigger>
              <Portal>
                <PopoverContent
                  w="220px"
                  bg={menuBg}
                  borderColor={borderColor}
                  boxShadow={shadow}
                  zIndex={9999}
                  _focus={{ boxShadow: shadow }}
                >
                  <PopoverBody p={0}>{userMenuContent}</PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          </Box>
        </CollapsedSidebarItem>
        <StockAlertsMenu collapsed />
        <CollapsedSidebarItem>
          <IconButton
            aria-label="Cambiar tema"
            size="sm"
            variant="ghost"
            onClick={toggleColorMode}
            icon={
              <Icon
                as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
                boxSize="18px"
              />
            }
          />
        </CollapsedSidebarItem>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap={1} w="100%" px={3}>
      <Menu placement="top-start" gutter={8} strategy="fixed">
        <MenuButton
          as={Button}
          variant="ghost"
          flex={1}
          minW={0}
          minH="44px"
          p={2}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          _hover={{ bg: hoverBg }}
          _active={{ bg: activeBg }}
        >
          <Flex align="center" gap={2} w="100%">
            <Avatar
              color="white"
              name={getUserInitials(currentUser)}
              bg="#003064"
              size="sm"
              w="32px"
              h="32px"
              flexShrink={0}
            />
            <Flex direction="column" align="flex-start" minW={0} flex={1}>
              <Text fontSize="xs" color={mutedColor} lineHeight="1.2">
                Sesión
              </Text>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={textColor}
                noOfLines={1}
                title={getDisplayName(currentUser)}
              >
                {getDisplayName(currentUser)}
              </Text>
            </Flex>
          </Flex>
        </MenuButton>
        <Portal>
          <MenuList
            zIndex={9999}
            boxShadow={shadow}
            p={0}
            borderRadius="16px"
            bg={menuBg}
            border="1px solid"
            borderColor={borderColor}
            minW="220px"
          >
            <Text
              px={4}
              pt={3}
              pb={2}
              fontSize="sm"
              fontWeight="700"
              color={textColor}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              Hola, {getDisplayName(currentUser)}
            </Text>
            <MenuItem
              color="red.500"
              py={3}
              onClick={handleLogout}
              _hover={{ bg: 'red.50' }}
              _focus={{ bg: 'red.50' }}
            >
              <Text fontSize="sm">Cerrar sesión</Text>
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>

      <StockAlertsMenu collapsed={false} />

      <IconButton
        aria-label="Cambiar tema"
        size="sm"
        variant="ghost"
        flexShrink={0}
        ml="auto"
        onClick={toggleColorMode}
        icon={
          <Icon
            as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
            boxSize="18px"
          />
        }
      />
    </Flex>
  );
}
